import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";

interface FormData {
  name: string;
  phone: string;
  email: string;
  lineId: string;
  location: string;
  landStatus: string;
  budget: string;
  timeline: string;
  description: string;
}

export default function FormSection() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "",
    email: "",
    lineId: "",
    location: "",
    landStatus: "",
    budget: "",
    timeline: "",
    description: "",
  });

  // Calculate progress
  const totalFields = 9;
  const filledFields = Object.values(formData).filter(
    (v) => v && v.trim() !== ""
  ).length;
  const progressPercent = Math.round((filledFields / totalFields) * 100);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const handleReset = () => {
    setSubmitted(false);
    setFormData({
      name: "",
      phone: "",
      email: "",
      lineId: "",
      location: "",
      landStatus: "",
      budget: "",
      timeline: "",
      description: "",
    });
  };

  if (submitted) {
    return (
      <section className="py-14 md:py-20 bg-warm-gray">
        <div className="container-main px-4 md:px-8 lg:px-12">
          <div className="max-w-xl mx-auto text-center bg-white rounded-2xl p-8 md:p-12 border border-warm-gray/30">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-champagne/10 mx-auto mb-5 animate-pulse">
              <span className="w-8 h-8 flex items-center justify-center">
                <i className="ri-check-line text-champagne text-2xl" />
              </span>
            </div>
            <h3 className="text-graphite mb-3">{t("contact.form.success")}</h3>
            <p className="text-medium-gray mb-6">
              {t("contact.form.lineTip")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://lin.ee/OpssTIM"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#06C755] text-white rounded-full text-base font-semibold transition-all duration-300 hover:bg-[#05b34d] hover:-translate-y-0.5 whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-line-fill" />
                </span>
                {t("contact.info.lineBtn")}
              </a>
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-graphite/20 text-graphite rounded-full text-base font-semibold transition-all duration-300 hover:bg-graphite/5 whitespace-nowrap cursor-pointer"
              >
                {t("contact.form.submit")}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-14 md:py-20 bg-warm-gray">
      <div className="container-main px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="tag-label mb-3 inline-block">{t("contact.form.label")}</span>
            <h2 className="mb-3">{t("contact.form.title")}</h2>
          </div>

          {/* Progress bar */}
          <div className="mb-6 md:mb-8 bg-white rounded-2xl p-4 md:p-5 border border-warm-gray/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-base font-medium text-graphite">填寫進度</span>
              <span className="text-base font-semibold text-champagne">{progressPercent}%</span>
            </div>
            <div className="w-full h-2 rounded-full bg-warm-gray/50 overflow-hidden">
              <div
                className="h-full rounded-full bg-champagne transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="text-base text-medium-gray mt-2">
              已填寫 {filledFields}/{totalFields} 個欄位，填完即可提交
            </p>
          </div>

          <form
            id="contactForm"
            data-readdy-form
            action="https://readdy.ai/api/form/d7ua6igb7i54bnk8qnp0"
            method="POST"
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-6 md:p-10 border border-warm-gray/30"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {/* 姓名 */}
              <div className={`transition-all duration-300 ${focusedField === "name" ? "scale-[1.01]" : ""}`}>
                <label htmlFor="name" className="block text-base font-medium text-graphite mb-1.5">
                  {t("contact.form.name")} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("name")}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-warm-gray/30 text-graphite text-base focus:outline-none focus:border-champagne focus:ring-2 focus:ring-champagne/10 transition-all"
                />
              </div>

              {/* 手機 */}
              <div className={`transition-all duration-300 ${focusedField === "phone" ? "scale-[1.01]" : ""}`}>
                <label htmlFor="phone" className="block text-base font-medium text-graphite mb-1.5">
                  {t("contact.form.phone")} <span className="text-red-400">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("phone")}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-warm-gray/30 text-graphite text-base focus:outline-none focus:border-champagne focus:ring-2 focus:ring-champagne/10 transition-all"
                />
              </div>

              {/* Email */}
              <div className={`transition-all duration-300 ${focusedField === "email" ? "scale-[1.01]" : ""}`}>
                <label htmlFor="email" className="block text-base font-medium text-graphite mb-1.5">
                  {t("contact.form.email")} <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-warm-gray/30 text-graphite text-base focus:outline-none focus:border-champagne focus:ring-2 focus:ring-champagne/10 transition-all"
                />
              </div>

              {/* LINE ID */}
              <div className={`transition-all duration-300 ${focusedField === "lineId" ? "scale-[1.01]" : ""}`}>
                <label htmlFor="lineId" className="block text-base font-medium text-graphite mb-1.5">
                  {t("contact.form.lineId")}
                </label>
                <input
                  type="text"
                  id="lineId"
                  name="lineId"
                  value={formData.lineId}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("lineId")}
                  onBlur={() => setFocusedField(null)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-warm-gray/30 text-graphite text-base focus:outline-none focus:border-champagne focus:ring-2 focus:ring-champagne/10 transition-all"
                />
              </div>

              {/* 基地位置 */}
              <div className={`transition-all duration-300 ${focusedField === "location" ? "scale-[1.01]" : ""}`}>
                <label htmlFor="location" className="block text-base font-medium text-graphite mb-1.5">
                  {t("contact.form.location")}
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("location")}
                  onBlur={() => setFocusedField(null)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-warm-gray/30 text-graphite text-base focus:outline-none focus:border-champagne focus:ring-2 focus:ring-champagne/10 transition-all"
                />
              </div>

              {/* 土地狀態 */}
              <div className={`transition-all duration-300 ${focusedField === "landStatus" ? "scale-[1.01]" : ""}`}>
                <label htmlFor="landStatus" className="block text-base font-medium text-graphite mb-1.5">
                  {t("contact.form.landStatus")}
                </label>
                <select
                  id="landStatus"
                  name="landStatus"
                  value={formData.landStatus}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("landStatus")}
                  onBlur={() => setFocusedField(null)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-warm-gray/30 text-graphite text-base focus:outline-none focus:border-champagne focus:ring-2 focus:ring-champagne/10 transition-all bg-white"
                >
                  <option value="">{t("contact.form.landStatus")}</option>
                  <option value="已有土地">{t("contact.form.landOption1")}</option>
                  <option value="家族土地">{t("contact.form.landOption2")}</option>
                  <option value="老屋 / 危老重建">{t("contact.form.landOption3")}</option>
                  <option value="正在找土地">{t("contact.form.landOption4")}</option>
                  <option value="其他">{t("contact.form.landOption5")}</option>
                </select>
              </div>

              {/* 預算級距 */}
              <div className={`transition-all duration-300 ${focusedField === "budget" ? "scale-[1.01]" : ""}`}>
                <label htmlFor="budget" className="block text-base font-medium text-graphite mb-1.5">
                  {t("contact.form.budget")}
                </label>
                <select
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("budget")}
                  onBlur={() => setFocusedField(null)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-warm-gray/30 text-graphite text-base focus:outline-none focus:border-champagne focus:ring-2 focus:ring-champagne/10 transition-all bg-white"
                >
                  <option value="">{t("contact.form.budget")}</option>
                  <option value="尚未確定">{t("contact.form.budgetOption1")}</option>
                  <option value="1000 萬以下">{t("contact.form.budgetOption2")}</option>
                  <option value="1000–2000 萬">{t("contact.form.budgetOption3")}</option>
                  <option value="2000–3000 萬">{t("contact.form.budgetOption4")}</option>
                  <option value="3000 萬以上">{t("contact.form.budgetOption5")}</option>
                </select>
              </div>

              {/* 預計啟動時間 */}
              <div className={`transition-all duration-300 ${focusedField === "timeline" ? "scale-[1.01]" : ""}`}>
                <label htmlFor="timeline" className="block text-base font-medium text-graphite mb-1.5">
                  {t("contact.form.timeline")}
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("timeline")}
                  onBlur={() => setFocusedField(null)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-warm-gray/30 text-graphite text-base focus:outline-none focus:border-champagne focus:ring-2 focus:ring-champagne/10 transition-all bg-white"
                >
                  <option value="">{t("contact.form.timeline")}</option>
                  <option value="越快越好">{t("contact.form.timelineOption1")}</option>
                  <option value="3 個月內">{t("contact.form.timelineOption2")}</option>
                  <option value="半年內">{t("contact.form.timelineOption3")}</option>
                  <option value="一年內">{t("contact.form.timelineOption4")}</option>
                  <option value="先了解流程">{t("contact.form.timelineOption5")}</option>
                </select>
              </div>
            </div>

            {/* 需求說明 */}
            <div className={`mt-4 md:mt-5 transition-all duration-300 ${focusedField === "description" ? "scale-[1.005]" : ""}`}>
              <label htmlFor="description" className="block text-base font-medium text-graphite mb-1.5">
                {t("contact.form.description")}
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                onFocus={() => setFocusedField("description")}
                onBlur={() => setFocusedField(null)}
                rows={5}
                maxLength={500}
                placeholder={t("contact.form.descriptionPlaceholder")}
                className="w-full px-4 py-3 rounded-lg border-2 border-warm-gray/30 text-graphite text-base focus:outline-none focus:border-champagne focus:ring-2 focus:ring-champagne/10 transition-all resize-none"
              />
              <p className="text-base text-medium-gray text-right mt-1">
                {formData.description.length}/500
              </p>
            </div>

            {/* Submit + LINE tip */}
            <div className="mt-6 md:mt-8">
              <button
                type="submit"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-champagne text-graphite rounded-full text-base font-semibold transition-all duration-300 hover:bg-champagne/90 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={filledFields < 3}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-send-plane-line" />
                </span>
                {t("contact.form.submit")}
              </button>

              {filledFields < 3 && (
                <p className="mt-2 text-base text-medium-gray">
                  請至少填寫姓名、手機與 Email 才能提交
                </p>
              )}

              <div className="mt-4 flex items-start gap-2 text-base text-medium-gray">
                <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-lightbulb-line text-champagne" />
                </span>
                <p>{t("contact.form.lineTip")}</p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}